import { CpuChipIcon, DocumentIcon } from '@heroicons/react/24/outline';

import { DemandExample } from '../../services/types/demand-example-types';
import { Badge } from '../ui/catalyst/badge';
import { Tooltip, TooltipContent, TooltipTrigger } from '../ui/tooltip';

interface DemandExampleProcessingStatusProps {
  example: DemandExample;
}

export const DemandExampleProcessingStatus = ({
  example,
}: DemandExampleProcessingStatusProps) => {
  // If already processed, show normal file badge
  if (example.processed) {
    if (example.demand) {
      return (
        <Tooltip>
          <TooltipTrigger asChild>
            <Badge
              color="green"
              className="flex items-center gap-1 hover:bg-green-600 hover:text-white transition-colors"
            >
              <DocumentIcon className="h-3 w-3" />
            </Badge>
          </TooltipTrigger>
          <TooltipContent>View demand file</TooltipContent>
        </Tooltip>
      );
    }
    return (
      <Tooltip>
        <TooltipTrigger>
          <span className="text-zinc-400">—</span>
        </TooltipTrigger>
        <TooltipContent>No file uploaded</TooltipContent>
      </Tooltip>
    );
  }

  // Processing states - processed = false
  const transformStatus = example.transform_details?.status;

  // If no transform yet or transform is in progress
  if (!transformStatus || transformStatus === 'IN_PROGRESS') {
    return (
      <Tooltip>
        <TooltipTrigger>
          <Badge color="blue" className="flex items-center gap-1">
            <DocumentIcon className="h-3 w-3 animate-pulse" />
          </Badge>
        </TooltipTrigger>
        <TooltipContent>Converting document to text...</TooltipContent>
      </Tooltip>
    );
  }

  // If transform completed but not yet processed (extraction phase)
  if (transformStatus === 'COMPLETED' && !example.processed) {
    return (
      <Tooltip>
        <TooltipTrigger>
          <Badge color="yellow" className="flex items-center gap-1">
            <CpuChipIcon className="h-3 w-3 animate-pulse" />
          </Badge>
        </TooltipTrigger>
        <TooltipContent>Extracting details and metadata...</TooltipContent>
      </Tooltip>
    );
  }

  // If transform failed
  if (transformStatus === 'FAILED') {
    return (
      <Tooltip>
        <TooltipTrigger>
          <Badge color="red" className="flex items-center gap-1">
            <DocumentIcon className="h-3 w-3" />
          </Badge>
        </TooltipTrigger>
        <TooltipContent>Document processing failed</TooltipContent>
      </Tooltip>
    );
  }

  // If transform was skipped
  if (transformStatus === 'SKIPPED') {
    return (
      <Tooltip>
        <TooltipTrigger>
          <Badge color="zinc" className="flex items-center gap-1">
            <DocumentIcon className="h-3 w-3" />
          </Badge>
        </TooltipTrigger>
        <TooltipContent>Document processing skipped</TooltipContent>
      </Tooltip>
    );
  }

  // Fallback - show processing
  return (
    <Tooltip>
      <TooltipTrigger>
        <Badge color="zinc" className="flex items-center gap-1">
          <DocumentIcon className="h-3 w-3 animate-pulse" />
        </Badge>
      </TooltipTrigger>
      <TooltipContent>Processing document...</TooltipContent>
    </Tooltip>
  );
};
